<template>
    <div class="product-attribute-row">
        <div class="d-flex align-items-center justify-content-between mb-25">
            <label :for="valueInputId" class="mb-0 font-weight-medium">
                {{ translatedTypeLabel(attribute.type) }}
            </label>
            <div class="d-flex align-items-center">
                <BButton
                    type="button"
                    variant="flat-primary"
                    size="sm"
                    class="mr-25"
                    @click="isChangingType = true"
                >
                    <FeatherIcon icon="Edit2Icon" size="13" class="mr-25" />
                    {{ translate('Component(ResearchReviewProcess):change-attribute-type', 'Change type') }}
                </BButton>
                <BButton
                    type="button"
                    variant="flat-danger"
                    size="sm"
                    class="btn-icon"
                    :disabled="!canDelete"
                    :title="removeTitle"
                    @click="$emit('remove')"
                >
                    <FeatherIcon icon="Trash2Icon" size="14" />
                </BButton>
            </div>
        </div>

        <div v-if="isChangingType" class="product-attribute-row__type-editor mb-75">
            <VSelect
                :value="selectedTypeOption"
                :options="translatedTypeOptions"
                :clearable="false"
                :placeholder="translate(
                    'Component(ResearchReviewProcess):select-attribute-type',
                    'Select attribute type',
                )"
                label="label"
                @input="changeType"
            />
            <BButton
                type="button"
                variant="flat-secondary"
                size="sm"
                @click="isChangingType = false"
            >
                {{ translate('Component(Button):cancel', 'Cancel') }}
            </BButton>
        </div>
        <small
            v-for="(message, messageIndex) in typeViolationMessages"
            :key="`type-violation-${messageIndex}`"
            class="text-danger d-block mb-25"
        >
            {{ message }}
        </small>

        <BFormInput
            :id="valueInputId"
            ref="valueInput"
            :value="attribute.value"
            :state="valueViolationMessages.length ? false : null"
            @input="updateAttribute('value', $event)"
        />
        <BFormInvalidFeedback
            v-for="(message, messageIndex) in valueViolationMessages"
            :key="`value-violation-${messageIndex}`"
            force-show
        >
            {{ message }}
        </BFormInvalidFeedback>

        <template v-if="showEvidence">
            <div class="d-flex align-items-center justify-content-between mt-75 mb-25">
                <label :for="evidenceInputId" class="mb-0 text-less-muted small">
                    {{ translate('Component(ResearchReviewProcess):evidence', 'Evidence') }}
                </label>
                <BButton
                    type="button"
                    variant="flat-danger"
                    size="sm"
                    @click="removeEvidence"
                >
                    {{ translate('Component(ResearchReviewProcess):remove-evidence', 'Remove evidence') }}
                </BButton>
            </div>
            <BFormTextarea
                :id="evidenceInputId"
                :value="attribute.evidence"
                :state="evidenceViolationMessages.length ? false : null"
                rows="2"
                max-rows="5"
                @input="updateAttribute('evidence', $event || null)"
            />
            <BFormInvalidFeedback
                v-for="(message, messageIndex) in evidenceViolationMessages"
                :key="`evidence-violation-${messageIndex}`"
                force-show
            >
                {{ message }}
            </BFormInvalidFeedback>
        </template>
        <BButton
            v-else
            type="button"
            variant="flat-primary"
            size="sm"
            class="mt-50 px-0"
            @click="showEvidence = true"
        >
            <FeatherIcon icon="PlusIcon" size="13" class="mr-25" />
            {{ translate('Component(ResearchReviewProcess):add-evidence', 'Add evidence') }}
        </BButton>
    </div>
</template>

<script>
import {
    BButton,
    BFormInput,
    BFormInvalidFeedback,
    BFormTextarea,
} from 'bootstrap-vue'
import VSelect from 'vue-select'
import {
    PRODUCT_ATTRIBUTE_TYPES,
    productAttributeTypeLabel,
} from './product-attributes'

export default {
    name: 'ProductAttributeRow',
    components: {
        BButton,
        BFormInput,
        BFormInvalidFeedback,
        BFormTextarea,
        VSelect,
    },
    props: {
        attribute: { type: Object, required: true },
        productIndex: { type: Number, required: true },
        attributeIndex: { type: Number, required: true },
        canDelete: { type: Boolean, default: true },
        autofocus: { type: Boolean, default: false },
        violations: { type: Array, default: () => [] },
    },
    data() {
        return {
            isChangingType: false,
            showEvidence: !!this.attribute.evidence,
        }
    },
    computed: {
        valueInputId() {
            return `product-${this.productIndex}-attribute-${this.attributeIndex}-value`
        },
        evidenceInputId() {
            return `product-${this.productIndex}-attribute-${this.attributeIndex}-evidence`
        },
        translatedTypeOptions() {
            return PRODUCT_ATTRIBUTE_TYPES.map(option => ({
                ...option,
                label: this.translatedTypeLabel(option.value),
            }))
        },
        selectedTypeOption() {
            return this.translatedTypeOptions.find(option => option.value === this.attribute.type)
                || null
        },
        typeViolationMessages() {
            return this.violationMessagesFor('type')
        },
        valueViolationMessages() {
            return this.violationMessagesFor('value')
        },
        evidenceViolationMessages() {
            return this.violationMessagesFor('evidence')
        },
        removeTitle() {
            return this.canDelete
                ? this.translate('Component(Button):remove', 'Remove')
                : this.translate(
                    'Component(ResearchReviewProcess):remove-product-instead',
                    'Remove the complete product to delete its last attribute.',
                )
        },
    },
    mounted() {
        if (this.autofocus) {
            this.focusValue()
        }
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        translatedTypeLabel(type) {
            const fallback = productAttributeTypeLabel(type)
            return this.translate(
                `Component(ResearchReviewProductAttributeType):${type}`,
                fallback,
            )
        },
        violationMessagesFor(field) {
            const suffix = `[${field}]`
            return this.violations
                .filter(violation => violation && String(violation.path || '').endsWith(suffix))
                .map(violation => violation.message)
                .filter(Boolean)
        },
        changeType(option) {
            if (!option) {
                return
            }
            this.isChangingType = false
            this.updateAttribute('type', option.value)
        },
        updateAttribute(key, value) {
            this.$emit('input', {
                ...this.attribute,
                [key]: value,
            })
        },
        removeEvidence() {
            this.showEvidence = false
            this.updateAttribute('evidence', null)
        },
        focusValue() {
            const { valueInput } = this.$refs
            if (valueInput && typeof valueInput.focus === 'function') {
                valueInput.focus()
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.product-attribute-row {
    padding: .75rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    background: #fafafa;
}

.product-attribute-row + .product-attribute-row {
    margin-top: .75rem;
}

.product-attribute-row__type-editor {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: .5rem;
}
</style>
